import React, { useState, useEffect } from 'react';
import AuthService from './services/authService';
import AuthModal from './components/AuthModal';

// Import Pages
import GlobalExpedyte from './pages/Campaign';
import BrowseRFQ from './pages/BrowseRFQ';
import PostRFQ from './pages/PostRFQ';
import SubmitProposal from './components/SubmitProposal';
import Contact from './pages/Contact';
import BusinessContact from './pages/BusinessContact';

// Import Style 
import './App.css';

function App() {
  // Get initial page from URL
  const getInitialPage = () => {
    const path = window.location.pathname;
    if (path === '/' || path === '/home') return 'home';
    if (path === '/browse-rfqs') return 'browse-rfqs';
    if (path === '/post-rfq') return 'post-rfq';
    if (path.startsWith('/submit-proposal')) return 'submit-proposal';
    if (path === '/contact') return 'contact';
    if (path === '/business-contact') return 'business-contact';
    return 'home';
  };

  const [currentPage, setCurrentPage] = useState(getInitialPage());
  const [selectedRfqId, setSelectedRfqId] = useState(null);
  
  // Global user state management
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authUserType, setAuthUserType] = useState('freelancer');
  const [authLoading, setAuthLoading] = useState(true);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPage(getInitialPage());
      window.scrollTo(0, 0);
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Load user on app start
  useEffect(() => {
    const loadUser = () => {
      try {
        const currentUser = AuthService.getCurrentUser();
        
        if (currentUser && AuthService.isAuthenticated()) {
          setUser(currentUser);
        }
      } catch (error) {
        console.error('Error loading user:', error);
      } finally {
        setAuthLoading(false);
      }
    };

    loadUser();

    // Listen for auth changes across the app
    const handleAuthChange = (event) => {
      if (event.detail.type === 'login') {
        setUser(event.detail.user);
      } else if (event.detail.type === 'logout') {
        setUser(null);
      }
    };

    window.addEventListener('authChange', handleAuthChange);
    
    return () => {
      window.removeEventListener('authChange', handleAuthChange);
    };
  }, []);

  // Global auth handlers
  const handleSignIn = (userType = 'freelancer') => {
    setAuthUserType(userType);
    setShowAuthModal(true);
  };

  const handleSignOut = () => {
    try {
      AuthService.logout();
      setUser(null);
      
      window.dispatchEvent(new CustomEvent('authChange', { 
        detail: { type: 'logout' } 
      }));
      
      showNotification('Signed out successfully!');
      navigateToHome();
    } catch (error) {
      console.error('Sign out error:', error);
      showNotification('Error signing out', 'error');
    }
  };

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    setShowAuthModal(false);
    
    window.dispatchEvent(new CustomEvent('authChange', { 
      detail: { type: 'login', user: userData } 
    }));
    
    showNotification(`Welcome, ${userData.firstName}!`);
  };

  // Simple notification function
  const showNotification = (message, type = 'success') => {
    const notification = document.createElement('div');
    const bgColor = type === 'success' ? '#003d7a' : '#dc2626';
    
    notification.style.cssText = `
      position: fixed;
      top: 100px;
      right: 20px;
      background: ${bgColor};
      color: white;
      padding: 16px 24px;
      border-radius: 0;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      z-index: 10000;
      animation: slideInRight 0.3s ease;
      font-weight: 600;
      font-size: 15px;
      max-width: 300px;
      border-left: 5px solid #ffb81c;
    `;
    
    notification.innerHTML = message;
    
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInRight {
        from {
          opacity: 0;
          transform: translateX(100px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
    `;
    document.head.appendChild(style);
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    }, 4000);
  };

  // ============================================
  // NAVIGATION HANDLERS WITH URL UPDATES
  // ============================================
  
  const navigateToHome = () => {
    setCurrentPage('home');
    setSelectedRfqId(null);
    window.history.pushState({}, '', '/home');
    document.title = 'Home | Global Expedyte - RFQ Marketplace';
    window.scrollTo(0, 0);
  };

  const navigateToBrowseRFQs = () => {
    setCurrentPage('browse-rfqs');
    setSelectedRfqId(null);
    window.history.pushState({}, '', '/browse-rfqs');
    document.title = 'Browse RFQs | Global Expedyte';
    window.scrollTo(0, 0);
  };

  const navigateToPostRFQ = () => {
    setCurrentPage('post-rfq');
    setSelectedRfqId(null);
    window.history.pushState({}, '', '/post-rfq');
    document.title = 'Post an RFQ | Global Expedyte';
    window.scrollTo(0, 0);
  };

  const navigateToSubmitProposal = (rfqId) => {
    setSelectedRfqId(rfqId);
    setCurrentPage('submit-proposal');
    window.history.pushState({}, '', `/submit-proposal/${rfqId}`);
    document.title = 'Submit Proposal | Global Expedyte';
    window.scrollTo(0, 0);
  };

  const navigateToContact = () => {
    setCurrentPage('contact');
    setSelectedRfqId(null);
    window.history.pushState({}, '', '/contact');
    document.title = 'Contact Us | Global Expedyte';
    window.scrollTo(0, 0);
  };

  const navigateToBusinessContact = () => {
    setCurrentPage('business-contact');
    setSelectedRfqId(null);
    window.history.pushState({}, '', '/business-contact');
    document.title = 'Business Onboarding | Global Expedyte';
    window.scrollTo(0, 0);
  };

  // ============================================
  // NAVIGATION PROPS HELPER
  // ============================================
  
  const getNavigationProps = () => ({
    // Navigation
    onNavigateToHome: navigateToHome,
    onNavigateToBrowseRFQs: navigateToBrowseRFQs,
    onNavigateToPostRFQ: navigateToPostRFQ,
    onNavigateToSubmitProposal: navigateToSubmitProposal,
    onNavigateToContact: navigateToContact,
    onNavigateToBusinessContact: navigateToBusinessContact,
    
    // User management
    user: user,
    onSignIn: handleSignIn,
    onSignOut: handleSignOut,
    
    // Current page for active state
    currentPage: currentPage
  });

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: '#f5f5f5'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '5px solid #e9ecef',
          borderTop: '5px solid #003d7a',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // ============================================
  // RENDER PAGES
  // ============================================

  return (
    <div className="App">
      {/* Home Page - Marketplace Homepage */}
      {currentPage === 'home' && (
        <GlobalExpedyte
          {...getNavigationProps()}
        />
      )}
      
      {/* Browse RFQs Page */}
      {currentPage === 'browse-rfqs' && (
        <BrowseRFQ
          {...getNavigationProps()}
        />
      )}

      {/* Post RFQ Page */}
      {currentPage === 'post-rfq' && (
        <PostRFQ
          {...getNavigationProps()}
        />
      )}

      {/* Submit Proposal Page */}
      {currentPage === 'submit-proposal' && (
        <SubmitProposal
          {...getNavigationProps()}
          rfqId={selectedRfqId}
        />
      )}

      {/* Contact Page */}
      {currentPage === 'contact' && (
        <Contact
          {...getNavigationProps()}
        />
      )}

      {/* Business Contact Page */}
      {currentPage === 'business-contact' && (
        <BusinessContact
          {...getNavigationProps()}
        />
      )}
      
      {/* Global Auth Modal */}
      {showAuthModal && (
        <AuthModal 
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
          userType={authUserType}
        />
      )}
    </div>
  );
}

export default App;